import { useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, redirect } from "@tanstack/react-router";
import { Check } from "lucide-react";
import { useEffect } from "react";
import { z } from "zod";

import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import {
  getOrganizationJoinFn,
  myOrganizationInviteQueryOptions,
} from "~/lib/functions/organization-invites";
import { createNoIndexMeta } from "~/lib/shared/meta";
import {
  inviteCredentialName,
  qualificationsAddParam,
  writeOrganizationInviteCookie,
} from "~/lib/shared/organization-invite";
import { setPostAuthCookies } from "~/lib/shared/post-auth-routing";

import { OrganizationMark } from "../../../_user/my-organization/-organization-mark";

/**
 * An organization's invite link. The organization sends it from its own
 * channels; it names the organization and the credential and sends the
 * graduate to the normal coach application. A link that names nothing
 * published falls back to the application with no invitation.
 */
export const Route = createFileRoute("/_public/organizations/$slug/join")({
  staticData: { title: "Join as a coach" },
  // A malformed `?program=` falls back to the organization alone being
  // refused below, never to an error page.
  validateSearch: z.object({
    program: z.coerce.string().optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => ({ program: search.program }),
  loader: async ({ params, deps }) => {
    const join = await getOrganizationJoinFn({
      data: { slug: params.slug, program: deps.program || undefined },
    });
    if (!join) throw redirect({ to: "/become-a-coach" });
    if (join.isCoach) {
      throw redirect({
        to: "/my-coaching/profile/qualifications",
        search: { add: qualificationsAddParam(join.invite) },
      });
    }
    return {
      title: `Join ${join.invite.organization.name} graduates`,
      invite: join.invite,
    };
  },
  head: ({ loaderData }) =>
    createNoIndexMeta(
      loaderData
        ? `${loaderData.title} on Dream Coach Match`
        : "Join as a coach – Dream Coach Match",
      "Create your coach profile on Dream Coach Match.",
    ),
  component: JoinPage,
});

function JoinPage(): React.JSX.Element {
  const { invite } = Route.useLoaderData();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const organization = invite.organization.name;
  const credential = inviteCredentialName(invite);

  // Written on load and again on click, so a visitor who signs up later in
  // the same browser still carries the invitation.
  useEffect(() => {
    writeOrganizationInviteCookie(invite);
  }, [invite]);

  const join = (): void => {
    writeOrganizationInviteCookie(invite);
    // The application reads the invitation fresh, not a cached "none".
    queryClient.removeQueries({
      queryKey: myOrganizationInviteQueryOptions().queryKey,
    });
    setPostAuthCookies({ intent: "coach", destination: "/become-a-coach" });
    void navigate({ to: "/become-a-coach" });
  };

  return (
    <div className="mx-auto max-w-xl px-4 py-12 sm:py-16">
      <Card className="space-y-6">
        <div className="flex items-center gap-4">
          <OrganizationMark
            name={organization}
            logoUrl={invite.organization.logoUrl}
          />
          <div>
            <p className="eyebrow text-purple-700">Invitation from</p>
            <p className="text-base font-medium text-gray-900">
              {organization}
            </p>
          </div>
        </div>
        <h1 className="font-heading text-2xl font-medium tracking-tight text-gray-900">
          Join {organization} graduates on Dream Coach Match
        </h1>
        <ul className="space-y-2">
          {[
            "A coach profile where people looking for a coach can find you",
            `${credential} added to your profile, for ${organization} to confirm`,
            `A "Verified by ${organization}" badge once they confirm it`,
          ].map((line) => (
            <li key={line} className="text-text-secondary flex gap-2 text-sm">
              <Check
                className="mt-0.5 size-4 shrink-0 text-purple-700"
                aria-hidden="true"
              />
              {line}
            </li>
          ))}
        </ul>
        <Button variant="primary" className="w-full" onClick={join}>
          Join as a coach
        </Button>
        <p className="text-text-tertiary text-sm">
          Already a coach on Dream Coach Match?{" "}
          <Link
            to="/my-coaching/profile/qualifications"
            search={{ add: qualificationsAddParam(invite) }}
            className="text-purple-700 underline underline-offset-2"
          >
            Add {credential} to your profile
          </Link>
        </p>
      </Card>
    </div>
  );
}
